import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { gameAPI } from '../services/gameAPI';
import { deleteFeaturedCase } from '../services/featuredCaseAPI';
import { featuredSessionId } from '../utils/featuredSession';
import { useGameStore } from '../store/gameStore';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Loader2, ArrowLeft, Clock, Calendar, Trash2, User, Play, Eye, LayoutGrid, List, Filter, X, AlertTriangle, RefreshCw, Info, Copy, Check, Users, Lock, Unlock, Shield, Infinity, Activity, Database, FileWarning, Image as ImageIcon } from 'lucide-react';
import { CaseInfoModal } from '../components/admin/CaseInfoModal';
import { UserDetailModal } from '../components/admin/UserDetailModal';
import { AdminOverviewCards } from '../components/admin/AdminOverviewCards';
import { AdminCasesTable } from '../components/admin/AdminCasesTable';
import { AdminReviewQueue } from '../components/admin/AdminReviewQueue';
import { AdminGenerationFailures } from '../components/admin/AdminGenerationFailures';
import { AdminImageAudit } from '../components/admin/AdminImageAudit';
import { getUserDisplayLabel, shortUserId } from '../utils/userDisplay';

// The difficulty an archive card SHOWS. Featured entries ship
// `display_difficulty` (backend `featured_cases/archive.py`) because they run
// on the easy mechanics but are labelled Medium for the player (SF-17);
// generated and tutorial entries carry no such field and are unaffected.
const displayDifficultyOf = (caseItem) => (
  caseItem.display_difficulty || caseItem.difficulty
);

export const CaseArchive = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSession, setLoadingSession] = useState(null);
  const [deletingSession, setDeletingSession] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // Admin can toggle: 'table' or 'cards'
  const [adminTab, setAdminTab] = useState('overview');

  // Admin review console state
  const [adminOverview, setAdminOverview] = useState(null);
  const [adminOverviewLoading, setAdminOverviewLoading] = useState(false);
  const [adminCases, setAdminCases] = useState([]);
  const [adminCasesTotal, setAdminCasesTotal] = useState(0);
  const [adminCasesLoading, setAdminCasesLoading] = useState(false);
  const [adminCasesFilters, setAdminCasesFilters] = useState({ limit: 100, offset: 0 });
  const [reviewCases, setReviewCases] = useState([]);
  const [reviewCasesTotal, setReviewCasesTotal] = useState(0);
  const [reviewCasesLoading, setReviewCasesLoading] = useState(false);
  const [reviewFilters, setReviewFilters] = useState({
    has_failures: '',
    has_images: '',
    limit: 100,
    offset: 0,
  });
  const [generationFailures, setGenerationFailures] = useState([]);
  const [generationFailuresTotal, setGenerationFailuresTotal] = useState(0);
  const [generationFailuresLoading, setGenerationFailuresLoading] = useState(false);
  const [generationFailureStats, setGenerationFailureStats] = useState(null);
  const [generationFailureStatsLoading, setGenerationFailureStatsLoading] = useState(false);
  const [generationFailureFilters, setGenerationFailureFilters] = useState({
    category: '',
    retryable: '',
    theme: '',
    case_id: '',
    limit: 100,
    offset: 0,
  });
  const [adminImages, setAdminImages] = useState([]);
  const [adminImagesTotal, setAdminImagesTotal] = useState(0);
  const [adminImagesLoading, setAdminImagesLoading] = useState(false);
  const [adminImageFilters, setAdminImageFilters] = useState({
    case_id: '',
    target_type: '',
    image_type: '',
    success: '',
    limit: 100,
    offset: 0,
  });

  // LLM Logs state (admin only)
  const [llmLogs, setLlmLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logCategories, setLogCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Case info modal state (admin only) - combined generation info + diagnostics
  const [showCaseInfo, setShowCaseInfo] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  // User detail modal state (admin only)
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Copy to clipboard state
  const [copiedId, setCopiedId] = useState(null);

  // Users tab state (admin only)
  const [usersData, setUsersData] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersFetched, setUsersFetched] = useState(false);
  const [togglingUser, setTogglingUser] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    owner: 'all', // 'all', 'mine', 'others'
    status: 'all', // 'all', 'in_progress', 'won', 'lost'
    theme: 'all', // 'all', 'noir', '90s_nyc', 'miami_vice'
    deleted: 'active', // 'all', 'active', 'deleted' (admin only)
  });
  const [showFilters, setShowFilters] = useState(true);

  const navigate = useNavigate();
  const loadSavedGame = useGameStore((state) => state.loadSavedGame);
  const resetGame = useGameStore((state) => state.resetGame);
  // No `sessionId` subscription: the one place that needs it reads it live
  // from `useGameStore.getState()` after its await, and nothing here renders
  // from it, so subscribing would only add re-renders.
  const { toast } = useToast();

  const fetchArchive = useCallback(async (shouldIgnore = () => false) => {
    try {
      setLoading(true);
      const data = await gameAPI.getCaseArchive();
      if (shouldIgnore()) return;
      setCases(data.cases || []);
      setIsAdmin(data.is_admin || false);
    } catch (error) {
      if (shouldIgnore()) return;
      console.error('Failed to fetch archive:', error);
      toast({
        title: 'Failed to load archive',
        description: error.message || 'Could not retrieve your cases',
        variant: 'destructive',
      });
    } finally {
      if (!shouldIgnore()) {
        setLoading(false);
      }
    }
  }, [toast]);

  useEffect(() => {
    let ignore = false;
    fetchArchive(() => ignore);
    return () => {
      ignore = true;
    };
  }, [fetchArchive]);

  // Fetch LLM logs (admin only)
  const fetchLLMLogs = useCallback(async (category = null, shouldIgnore = () => false) => {
    if (!isAdmin) return;
    try {
      setLogsLoading(true);
      const data = await gameAPI.getLLMLogs(category, null, 100, 0);
      if (shouldIgnore()) return;
      setLlmLogs(data.logs || []);
      setLogsTotal(data.total || 0);
      setLogCategories(data.categories || {});
    } catch (error) {
      if (shouldIgnore()) return;
      console.error('Failed to fetch LLM logs:', error);
      toast({
        title: 'Failed to load logs',
        description: error.message || 'Could not retrieve LLM logs',
        variant: 'destructive',
      });
    } finally {
      if (!shouldIgnore()) {
        setLogsLoading(false);
      }
    }
  }, [isAdmin, toast]);

  // Fetch users list (admin only)
  const fetchUsers = useCallback(async (shouldIgnore = () => false) => {
    if (!isAdmin) return;
    try {
      setUsersLoading(true);
      const data = await gameAPI.getUsersTutorialStatus();
      if (shouldIgnore()) return;
      setUsersData(data.users || []);
      setUsersFetched(true);
    } catch (error) {
      if (shouldIgnore()) return;
      console.error('Failed to fetch users:', error);
      toast({
        title: 'Failed to load users',
        description: error.message || 'Could not retrieve user data',
        variant: 'destructive',
      });
    } finally {
      if (!shouldIgnore()) {
        setUsersLoading(false);
      }
    }
  }, [isAdmin, toast]);

  const fetchAdminOverview = useCallback(async (shouldIgnore = () => false) => {
    if (!isAdmin) return;
    try {
      setAdminOverviewLoading(true);
      const data = await gameAPI.getAdminCasesOverview();
      if (shouldIgnore()) return;
      setAdminOverview(data || null);
    } catch (error) {
      if (shouldIgnore()) return;
      console.error('Failed to fetch admin overview:', error);
      toast({
        title: 'Failed to load overview',
        description: error.response?.data?.detail || error.message || 'Could not retrieve admin overview',
        variant: 'destructive',
      });
    } finally {
      if (!shouldIgnore()) {
        setAdminOverviewLoading(false);
      }
    }
  }, [isAdmin, toast]);

  const fetchAdminCases = useCallback(async (activeFilters = adminCasesFilters, shouldIgnore = () => false) => {
    if (!isAdmin) return;
    try {
      setAdminCasesLoading(true);
      const data = await gameAPI.getAdminCases(activeFilters);
      if (shouldIgnore()) return;
      const rows = Array.isArray(data) ? data : data.cases || data.rows || data.items || [];
      setAdminCases(rows);
      setAdminCasesTotal(Array.isArray(data) ? rows.length : data.total || rows.length || 0);
    } catch (error) {
      if (shouldIgnore()) return;
      console.error('Failed to fetch admin cases:', error);
      toast({
        title: 'Failed to load admin cases',
        description: error.response?.data?.detail || error.message || 'Could not retrieve admin case health',
        variant: 'destructive',
      });
    } finally {
      if (!shouldIgnore()) {
        setAdminCasesLoading(false);
      }
    }
  }, [adminCasesFilters, isAdmin, toast]);

  const fetchReviewCases = useCallback(async (activeFilters = reviewFilters, shouldIgnore = () => false) => {
    if (!isAdmin) return;
    try {
      setReviewCasesLoading(true);
      const requestFilters = { ...activeFilters };
      delete requestFilters.has_failures;
      delete requestFilters.has_images;
      const reviewStatuses = ['needs_review', 'investigating', 'resolved', 'false_positive'];
      const responses = await Promise.all(reviewStatuses.map((reviewStatus) => (
        gameAPI.getAdminCases({
          ...requestFilters,
          review_status: reviewStatus,
        })
      )));
      if (shouldIgnore()) return;
      const rowsByCaseId = new Map();
      let combinedTotal = 0;
      responses.forEach((data) => {
        const rows = Array.isArray(data) ? data : data.cases || data.rows || data.items || [];
        combinedTotal += Array.isArray(data) ? rows.length : data.total || rows.length || 0;
        rows.forEach((row) => {
          const caseId = row.case_id || row.id;
          if (caseId && !rowsByCaseId.has(caseId)) {
            rowsByCaseId.set(caseId, row);
          }
        });
      });
      setReviewCases(Array.from(rowsByCaseId.values()));
      setReviewCasesTotal(combinedTotal);
    } catch (error) {
      if (shouldIgnore()) return;
      console.error('Failed to fetch review queue:', error);
      toast({
        title: 'Failed to load review queue',
        description: error.response?.data?.detail || error.message || 'Could not retrieve cases needing review',
        variant: 'destructive',
      });
    } finally {
      if (!shouldIgnore()) {
        setReviewCasesLoading(false);
      }
    }
  }, [isAdmin, reviewFilters, toast]);

  const fetchGenerationFailures = useCallback(async (activeFilters = generationFailureFilters, shouldIgnore = () => false) => {
    if (!isAdmin) return;
    try {
      setGenerationFailuresLoading(true);
      const data = await gameAPI.getAdminGenerationFailures(activeFilters);
      if (shouldIgnore()) return;
      setGenerationFailures(data.failures || []);
      setGenerationFailuresTotal(data.total || 0);
    } catch (error) {
      if (shouldIgnore()) return;
      console.error('Failed to fetch generation failures:', error);
      toast({
        title: 'Failed to load generation failures',
        description: error.response?.data?.detail || error.message || 'Could not retrieve generation failure diagnostics',
        variant: 'destructive',
      });
    } finally {
      if (!shouldIgnore()) {
        setGenerationFailuresLoading(false);
      }
    }
  }, [generationFailureFilters, isAdmin, toast]);

  const fetchGenerationFailureStats = useCallback(async (shouldIgnore = () => false) => {
    if (!isAdmin) return;
    try {
      setGenerationFailureStatsLoading(true);
      const data = await gameAPI.getAdminGenerationFailureStats();
      if (shouldIgnore()) return;
      setGenerationFailureStats(data || null);
    } catch (error) {
      if (shouldIgnore()) return;
      console.error('Failed to fetch generation failure stats:', error);
      toast({
        title: 'Failed to load failure stats',
        description: error.response?.data?.detail || error.message || 'Could not retrieve failure stats',
        variant: 'destructive',
      });
    } finally {
      if (!shouldIgnore()) {
        setGenerationFailureStatsLoading(false);
      }
    }
  }, [isAdmin, toast]);

  const fetchAdminImages = useCallback(async (activeFilters = adminImageFilters, shouldIgnore = () => false) => {
    if (!isAdmin) return;
    try {
      setAdminImagesLoading(true);
      const data = await gameAPI.getAdminImages(activeFilters);
      if (shouldIgnore()) return;
      setAdminImages(data.images || []);
      setAdminImagesTotal(data.total || 0);
    } catch (error) {
      if (shouldIgnore()) return;
      console.error('Failed to fetch admin images:', error);
      toast({
        title: 'Failed to load image audit',
        description: error.response?.data?.detail || error.message || 'Could not retrieve image audit rows',
        variant: 'destructive',
      });
    } finally {
      if (!shouldIgnore()) {
        setAdminImagesLoading(false);
      }
    }
  }, [adminImageFilters, isAdmin, toast]);

  // Fetch logs when admin switches to logs tab
  useEffect(() => {
    if (isAdmin && adminTab === 'logs') {
      let ignore = false;
      fetchLLMLogs(selectedCategory, () => ignore);
      return () => {
        ignore = true;
      };
    }
    return undefined;
  }, [isAdmin, adminTab, selectedCategory, fetchLLMLogs]);

  // Fetch users when admin switches to users tab (lazy load)
  useEffect(() => {
    if (isAdmin && adminTab === 'users' && !usersFetched) {
      let ignore = false;
      fetchUsers(() => ignore);
      return () => {
        ignore = true;
      };
    }
    return undefined;
  }, [isAdmin, adminTab, usersFetched, fetchUsers]);

  useEffect(() => {
    if (isAdmin && (adminTab === 'overview' || adminTab === 'cases')) {
      let ignore = false;
      if (adminTab === 'overview') {
        fetchAdminOverview(() => ignore);
      }
      fetchAdminCases(adminCasesFilters, () => ignore);
      return () => {
        ignore = true;
      };
    }
    return undefined;
  }, [isAdmin, adminTab, adminCasesFilters, fetchAdminOverview, fetchAdminCases]);

  useEffect(() => {
    if (isAdmin && adminTab === 'review') {
      let ignore = false;
      fetchReviewCases(reviewFilters, () => ignore);
      return () => {
        ignore = true;
      };
    }
    return undefined;
  }, [isAdmin, adminTab, reviewFilters, fetchReviewCases]);

  useEffect(() => {
    if (isAdmin && adminTab === 'failures') {
      let ignore = false;
      fetchGenerationFailures(generationFailureFilters, () => ignore);
      fetchGenerationFailureStats(() => ignore);
      return () => {
        ignore = true;
      };
    }
    return undefined;
  }, [isAdmin, adminTab, generationFailureFilters, fetchGenerationFailures, fetchGenerationFailureStats]);

  useEffect(() => {
    if (isAdmin && adminTab === 'images') {
      let ignore = false;
      fetchAdminImages(adminImageFilters, () => ignore);
      return () => {
        ignore = true;
      };
    }
    return undefined;
  }, [isAdmin, adminTab, adminImageFilters, fetchAdminImages]);

  // Filter cases based on current filters
  const filteredCases = useMemo(() => {
    return cases.filter(caseItem => {
      // Owner filter
      if (filters.owner === 'mine' && !caseItem.is_own_case) return false;
      if (filters.owner === 'others' && caseItem.is_own_case) return false;

      // Status filter
      if (filters.status !== 'all' && caseItem.status !== filters.status) return false;

      // Theme filter
      if (filters.theme !== 'all' && caseItem.theme !== filters.theme) return false;

      // Deleted filter (admin only)
      if (filters.deleted === 'active' && caseItem.deleted_by_user) return false;
      if (filters.deleted === 'deleted' && !caseItem.deleted_by_user) return false;

      return true;
    });
  }, [cases, filters]);

  // Check if any filters are active
  const hasActiveFilters = filters.owner !== 'all' || filters.status !== 'all' || filters.theme !== 'all' || filters.deleted !== 'active';

  // Count active filters (treat 'deleted: active' as no-filter, not 'all')
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.owner !== 'all') count++;
    if (filters.status !== 'all') count++;
    if (filters.theme !== 'all') count++;
    if (filters.deleted !== 'all' && filters.deleted !== 'active') count++;
    return count;
  }, [filters]);

  // Clear all filters
  const clearFilters = () => {
    setFilters({ owner: 'all', status: 'all', theme: 'all', deleted: 'active' });
  };

  // Count deleted cases (admin only)
  const deletedCasesCount = useMemo(() => {
    return cases.filter(c => c.deleted_by_user).length;
  }, [cases]);

  // Calculate safe progress percentage
  const getProgressPercent = (turnCount, maxTurns) => {
    if (!maxTurns || maxTurns <= 0 || !Number.isFinite(maxTurns)) return 0;
    if (!turnCount || !Number.isFinite(turnCount)) return 0;
    const percent = (turnCount / maxTurns) * 100;
    return Math.min(Math.max(percent, 0), 100);
  };

  const handleLoadCase = async (caseItem) => {
    const sessionId = caseItem.session_id;
    if (caseItem.source === 'featured' && caseItem.is_own_case === false) return;

    try {
      setLoadingSession(sessionId);

      if (caseItem.source === 'featured') {
        navigate(`/featured/${encodeURIComponent(caseItem.slug)}`);
        return;
      }

      const savedData = await gameAPI.loadGameSession(sessionId);

      // Load into store
      loadSavedGame({
        theme: savedData.theme || 'noir',
        difficulty: savedData.difficulty || 'medium',
        caseId: savedData.caseId,
        sessionId: savedData.sessionId,
        gameState: savedData.gameState,
        messageHistory: savedData.messageHistory,
        guideCharacter: savedData.guideCharacter,
        caseData: savedData.caseData,
        lastImage: savedData.lastImage,
        lastImageVantage2: savedData.lastImageVantage2
      });

      // Navigate to game
      navigate('/game');
    } catch (error) {
      console.error('Failed to load case:', error);
      toast({
        title: 'Failed to load case',
        description: error.response?.data?.detail || error.message || 'Could not load the selected case',
        variant: 'destructive',
      });
    } finally {
      setLoadingSession(null);
    }
  };

  const handleDeleteCase = async (caseItem) => {
    const sessionId = caseItem.session_id;
    const victimName = caseItem.victim_name;

    // Generated-case copy is intentionally unchanged. Featured deletion only
    // removes one player's progress; the authored case remains in the catalog.
    const confirmMessage = caseItem.source === 'featured'
      ? isAdmin
        ? `PERMANENTLY remove ${(caseItem.owner_display_name || 'this player')}'s progress for featured case "${victimName}"?\n\nThe featured case itself will stay available for players.\n\nThis cannot be undone.`
        : `Remove your progress for featured case "${victimName}"?\n\nThe featured case will stay available, and you can start it again from the beginning.`
      : isAdmin
        ? `PERMANENTLY delete the case "${victimName}"?\n\nThis will remove:\n• The game session\n• All conversation history\n• The case data (if no other sessions use it)\n\nThis cannot be undone.`
        : `Remove the case "${victimName}" from your archive?\n\nThe case will be hidden from your view but can be restored by an admin if needed.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeletingSession(sessionId);
      // Own-progress deletion (generated or featured) goes through gameAPI,
      // which routes a `featured:{slug}` id onto the compat archive route.
      // NOTE: the archive card's own `session_id` is `featured:{owner}:{slug}`
      // (backend/featured_cases/archive.py:14-17), which is NOT the id the
      // compat layer answers to, so the id is rebuilt from the slug here.
      // The admin cross-owner delete uses the non-compat `/static-cases/{slug}`
      // route instead: the compat route has no `owner_id` parameter.
      const deleteTargetId = caseItem.source === 'featured'
        ? featuredSessionId(caseItem.slug)
        : sessionId;
      const response = caseItem.source === 'featured' && isAdmin && caseItem.owner_id
        ? await deleteFeaturedCase(caseItem.slug, caseItem.owner_id)
        : await gameAPI.deleteGameSession(deleteTargetId);

      // The server record is gone, so the in-memory run has to go with it.
      // Otherwise the previous playthrough keeps rendering: `GamePage` only
      // refetches when the stored payload is NOT authoritative
      // (utils/gameHydrationGuards.js:13-19), and a complete run-1 payload IS
      // authoritative — so an in-tab return to `/game` would show run-1
      // consults, news, notes and message history against a record that no
      // longer exists. A featured session id is `featured:{slug}` and is
      // identical across restarts, which is what makes this reachable at all.
      // `resetGame` is the same door the other reset paths use
      // (ActionBar.jsx:555, CaseSummaryModal.jsx:75); a reset store has no
      // session id, so the next entry refetches from the server.
      //
      // Only ever the case the player is actually holding. The ownership test
      // belongs to FEATURED rows alone: their compat id is `featured:{slug}`,
      // which is owner-agnostic, so an admin deleting someone else's featured
      // progress would otherwise reset their own identical-id session. A
      // generated session id is unique to one session, so an id match there
      // IS the active session and must reset regardless of who owns the row.
      // Read the id LIVE, not from render: the delete above is awaited, and
      // the player can have started or loaded another case in the meantime.
      // The render-time snapshot would then either reset a session that is no
      // longer the deleted one, or miss the one that is.
      const currentSessionId = useGameStore.getState().sessionId;
      const deletedTheActiveSession = Boolean(currentSessionId)
        && currentSessionId === deleteTargetId
        && (caseItem.source !== 'featured' || caseItem.is_own_case !== false);
      if (deletedTheActiveSession) {
        resetGame();
      }

      if (response.hard_delete) {
        // Admin hard delete - remove from local state
        setCases(prev => prev.filter(c => c.session_id !== sessionId));
        toast({
          title: 'Case permanently deleted',
          description: `Deleted session, ${response.deleted?.messages || 0} messages${response.deleted?.case ? ', and case data' : ''}`,
        });
      } else {
        // User soft delete - remove from local state (backend filters it out anyway)
        setCases(prev => prev.filter(c => c.session_id !== sessionId));
        toast({
          title: 'Case removed',
          description: 'The case has been removed from your archive',
        });
      }
    } catch (error) {
      console.error('Failed to delete case:', error);
      toast({
        title: 'Failed to delete case',
        description: error.response?.data?.detail || error.message || 'Could not delete the case',
        variant: 'destructive',
      });
    } finally {
      setDeletingSession(null);
    }
  };

  // Open case info modal (admin only)
  const handleViewCaseInfo = (caseItem) => {
    if (caseItem.source === 'featured') {
      return;
    }

    setSelectedCaseId(caseItem.case_id);
    setSelectedSessionId(caseItem.session_id);
    setShowCaseInfo(true);
  };

  const handleGeneratedCaseInfo = (caseItemOrId, sessionId) => {
    const caseItem = typeof caseItemOrId === 'object'
      ? caseItemOrId
      : { case_id: caseItemOrId, session_id: sessionId };
    handleViewCaseInfo(caseItem);
  };

  const getAdminCaseItem = (sessionId) => {
    const caseItem = adminCases.find((item) => (
      (item.session_id || item.sessionId) === sessionId
    ));
    if (!caseItem) return null;

    return {
      ...caseItem,
      case_id: caseItem.case_id || caseItem.id,
      session_id: caseItem.session_id || caseItem.sessionId,
      victim_name: caseItem.victim_name || caseItem.victim?.name || 'Unknown victim',
    };
  };

  const handleLoadAdminCase = (sessionId) => {
    const caseItem = getAdminCaseItem(sessionId);
    if (caseItem) handleLoadCase(caseItem);
  };

  const handleDeleteAdminCase = (sessionId) => {
    const caseItem = getAdminCaseItem(sessionId);
    if (caseItem) handleDeleteCase(caseItem);
  };

  // Handle errors from case info modal (memoized for stable reference)
  const handleCaseInfoError = useCallback((message) => {
    toast({
      title: 'Error',
      description: message,
      variant: 'destructive',
    });
  }, [toast]);

  // Close case info modal (memoized for stable reference)
  const handleCloseCaseInfo = useCallback(() => {
    setShowCaseInfo(false);
    setSelectedCaseId(null);
    setSelectedSessionId(null);
  }, []);

  // Open user detail modal
  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserDetail(true);
  };

  // Close user detail modal
  const handleCloseUserDetail = useCallback(() => {
    setShowUserDetail(false);
    setSelectedUser(null);
  }, []);

  // Handle user data update from modal
  const handleUserUpdated = useCallback((updatedUser) => {
    setUsersData(prev => prev.map(u =>
      u.user_id === updatedUser.user_id ? { ...u, ...updatedUser } : u
    ));
    setSelectedUser(prev => prev ? { ...prev, ...updatedUser } : null);
  }, []);

  // Copy case ID to clipboard
  const handleCopyId = async (id, type = 'case') => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      toast({
        title: 'Copied!',
        description: `${type === 'case' ? 'Case' : type === 'user' ? 'User' : 'Session'} ID copied to clipboard`,
      });
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getThemeColors = (theme, isTutorial = false, source = 'generated') => {
    if (isTutorial) {
      return 'bg-emerald-600 text-white';
    }
    if (source === 'featured') {
      return 'bg-amber-600 text-white';
    }
    const colors = {
      noir: 'bg-gray-800 text-gray-100',
      '90s_nyc': 'bg-orange-600 text-white',
      miami_vice: 'bg-pink-500 text-white'
    };
    return colors[theme] || colors.noir;
  };

  const getStatusBadge = (status) => {
    const variants = {
      'in_progress': { className: 'bg-blue-600 text-white', label: 'In Progress' },
      'won': { className: 'bg-green-600 text-white', label: 'Solved' },
      'lost': { className: 'bg-red-600 text-white', label: 'Failed' }
    };
    return variants[status] || variants['in_progress'];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    // Ensure UTC dates without 'Z' suffix are parsed correctly as UTC
    let normalizedDate = dateString;
    if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+')) {
      normalizedDate = dateString + 'Z';
    }
    return new Date(normalizedDate).toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Unknown';
    // Ensure UTC dates without 'Z' suffix are parsed correctly as UTC
    // Python's datetime.utcnow().isoformat() doesn't include 'Z'
    let normalizedDate = dateString;
    if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+')) {
      normalizedDate = dateString + 'Z';
    }
    return new Date(normalizedDate).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatThemeName = (theme, isTutorial = false, source = 'generated') => {
    if (isTutorial) {
      return 'Tutorial';
    }
    const names = {
      noir: '1940s Noir',
      '90s_nyc': '90s NYC',
      miami_vice: '80s Miami'
    };
    const themeName = names[theme] || theme;
    return source === 'featured' ? `Featured · ${themeName}` : themeName;
  };

  // Calculate stats for admin dashboard (based on ALL cases, not filtered)
  const getStats = () => {
    const total = cases.length;
    const inProgress = cases.filter(c => c.status === 'in_progress').length;
    const solved = cases.filter(c => c.status === 'won').length;
    const failed = cases.filter(c => c.status === 'lost').length;
    const deleted = cases.filter(c => c.deleted_by_user).length;
    const uniqueUsers = new Set(cases.map(c => c.owner_id).filter(Boolean)).size;
    return { total, inProgress, solved, failed, deleted, uniqueUsers };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  // Filter button component
  const FilterButton = ({ label, filterKey, options }) => (
    <div className="flex items-center gap-1">
      <span className="text-xs text-gray-500 uppercase tracking-wide">{label}:</span>
      <div className="flex gap-1">
        {options.map(opt => (
          <button
            key={opt.value}
            onClick={() => setFilters(f => ({ ...f, [filterKey]: opt.value }))}
            className={`px-2 py-1 text-xs rounded transition-colors ${
              filters[filterKey] === opt.value
                ? 'bg-white/20 text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/10'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );

  // Filters bar component (admin only)
  const renderFilters = () => (
    <div className="bg-gray-800/30 rounded-lg border border-gray-700/50 p-3 mb-4">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-400">Filters</span>
        </div>

        <FilterButton
          label="Owner"
          filterKey="owner"
          options={[
            { value: 'all', label: 'All' },
            { value: 'mine', label: 'Mine' },
            { value: 'others', label: 'Others' },
          ]}
        />

        <FilterButton
          label="Status"
          filterKey="status"
          options={[
            { value: 'all', label: 'All' },
            { value: 'in_progress', label: 'In Progress' },
            { value: 'won', label: 'Solved' },
            { value: 'lost', label: 'Failed' },
          ]}
        />

        <FilterButton
          label="Theme"
          filterKey="theme"
          options={[
            { value: 'all', label: 'All' },
            { value: 'noir', label: 'Noir' },
            { value: '90s_nyc', label: '90s NYC' },
            { value: 'miami_vice', label: '80s Miami' },
          ]}
        />

        {/* Deleted filter - admin only */}
        {isAdmin && deletedCasesCount > 0 && (
          <FilterButton
            label="Visibility"
            filterKey="deleted"
            options={[
              { value: 'active', label: 'Active' },
              { value: 'deleted', label: `Deleted (${deletedCasesCount})` },
              { value: 'all', label: 'All' },
            ]}
          />
        )}

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-colors ml-auto"
          >
            <X className="w-3 h-3" />
            Clear
          </button>
        )}
      </div>

      {hasActiveFilters && (
        <div className="mt-2 text-xs text-gray-500">
          Showing {filteredCases.length} of {cases.length} cases
        </div>
      )}
    </div>
  );

  // Regular user card view
  const renderUserCardView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
      {cases.map((caseItem) => {
        const statusInfo = getStatusBadge(caseItem.status);
        const isForeignFeatured = caseItem.source === 'featured' && caseItem.is_own_case === false;

        return (
          <Card
            key={caseItem.session_id}
            className="bg-gray-900 border-gray-700 hover:border-gray-500 transition-colors"
          >
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <CardTitle className="text-xl text-white">
                  {caseItem.victim_name}
                </CardTitle>
                <Badge className={statusInfo.className}>
                  {statusInfo.label}
                </Badge>
              </div>
            </CardHeader>

            <CardContent>
              {/* Theme & Difficulty */}
              <div className="flex gap-2 mb-3">
                <Badge className={getThemeColors(caseItem.theme, caseItem.is_tutorial, caseItem.source)}>
                  {formatThemeName(caseItem.theme, caseItem.is_tutorial, caseItem.source)}
                </Badge>
                <Badge variant="outline" className="capitalize text-gray-300 border-gray-600">
                  {displayDifficultyOf(caseItem)}
                </Badge>
              </div>

              {/* Progress */}
              <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Turn {caseItem.turn_count || 0}/{caseItem.max_turns || 30}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(caseItem.started_at)}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  className="flex-1 min-h-touch"
                  onClick={() => handleLoadCase(caseItem)}
                  disabled={isForeignFeatured || loadingSession === caseItem.session_id || deletingSession === caseItem.session_id}
                  title={isForeignFeatured ? 'Featured progress belongs to another player' : undefined}
                >
                  {loadingSession === caseItem.session_id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : caseItem.status === 'in_progress' ? (
                    'Continue'
                  ) : (
                    'Review'
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="min-h-touch min-w-touch border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
                  onClick={() => handleDeleteCase(caseItem)}
                  disabled={loadingSession === caseItem.session_id || deletingSession === caseItem.session_id}
                >
                  {deletingSession === caseItem.session_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  // Admin LLM logs view
  const renderLogsView = () => {
    const categoryLabels = {
      invalid_suspect: 'Invalid Suspect ID',
      invalid_location: 'Invalid Location ID',
      invalid_document: 'Invalid Document ID',
      invalid_clue: 'Invalid Clue ID',
      invalid_clue_location: 'Invalid Clue Location',
      parse_error: 'Parse Error',
      consistency: 'Consistency Issue',
    };

    const formatTimestamp = (ts) => {
      if (!ts) return 'Unknown';
      return new Date(ts).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    };

    return (
      <div className="space-y-4">
        {/* Category filter buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-400">Filter:</span>
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-3 py-1.5 text-sm rounded transition-colors ${
              !selectedCategory ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            All ({logsTotal})
          </button>
          {Object.entries(logCategories).map(([cat, count]) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 text-sm rounded transition-colors ${
                selectedCategory === cat ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {categoryLabels[cat] || cat} ({count})
            </button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchLLMLogs(selectedCategory)}
            disabled={logsLoading}
            className="ml-auto"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${logsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Logs list */}
        {logsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : llmLogs.length === 0 ? (
          <div className="text-center py-12 bg-gray-900/30 rounded-lg border border-gray-700">
            <AlertTriangle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No LLM warnings found.</p>
            <p className="text-sm text-gray-500 mt-1">This is good! It means the LLM is returning valid data.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {llmLogs.map((log) => (
              <div
                key={log._id}
                className="bg-gray-900/50 rounded-lg border border-gray-700 p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-amber-600 text-white text-xs">
                        {categoryLabels[log.category] || log.category}
                      </Badge>
                      <span className="text-xs text-gray-500">{formatTimestamp(log.timestamp)}</span>
                    </div>
                    <p className="text-sm text-white break-words">{log.message}</p>
                    {log.session_id && (
                      <p className="text-xs text-gray-500 mt-1">Session: {log.session_id.slice(-8)}</p>
                    )}
                    {log.details && Object.keys(log.details).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300">
                          Show details
                        </summary>
                        <pre className="mt-1 text-xs text-gray-400 bg-black/30 p-2 rounded overflow-x-auto">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Handle toggle tutorial unlock for a user
  const handleToggleTutorial = async (user, currentUnlocked, isAdminUser) => {
    const userId = user?.user_id;
    if (isAdminUser) return; // Don't allow toggling admin users
    const newUnlocked = !currentUnlocked;
    const userLabel = getUserDisplayLabel({ id: userId, displayName: user?.display_name });
    try {
      setTogglingUser(userId);
      // Optimistic update
      setUsersData(prev => prev.map(u =>
        u.user_id === userId ? { ...u, case_generation_unlocked: newUnlocked } : u
      ));
      await gameAPI.toggleUserTutorial(userId, newUnlocked);
      toast({
        title: newUnlocked ? 'Case generation unlocked' : 'Case generation locked',
        description: `${userLabel} has been ${newUnlocked ? 'unlocked' : 'locked'}`,
      });
    } catch (error) {
      // Revert optimistic update
      setUsersData(prev => prev.map(u =>
        u.user_id === userId ? { ...u, case_generation_unlocked: currentUnlocked } : u
      ));
      console.error('Failed to toggle tutorial:', error);
      toast({
        title: 'Failed to update user',
        description: error.response?.data?.detail || error.message || 'Could not update tutorial status',
        variant: 'destructive',
      });
    } finally {
      setTogglingUser(null);
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Never';
    let normalizedDate = dateString;
    if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+')) {
      normalizedDate = dateString + 'Z';
    }
    const date = new Date(normalizedDate);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  // Admin users view
  const renderUsersView = () => {
    if (usersLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      );
    }

    if (usersData.length === 0) {
      return (
        <div className="text-center py-12 bg-gray-900/30 rounded-lg border border-gray-700">
          <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No users found.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-400">{usersData.length} user{usersData.length !== 1 ? 's' : ''}</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setUsersFetched(false); fetchUsers(); }}
            disabled={usersLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${usersLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="bg-gray-900/50 rounded-lg border border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700 bg-gray-800/50">
                  <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">User</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Tutorial</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Case Gen</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Role</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Daily Limit</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Today</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Total Cases</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Last Active</th>
                  <th className="text-right p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {usersData.map((user) => {
                  const isToggling = togglingUser === user.user_id;
                  const userLabel = getUserDisplayLabel({
                    id: user.user_id,
                    displayName: user.display_name,
                  });
                  const hasDisplayName = Boolean(user.display_name?.trim());
                  return (
                    <tr
                      key={user.user_id}
                      className="hover:bg-gray-800/30 transition-colors"
                    >
                      <td className="p-3">
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => handleViewUser(user)}
                            className={`w-fit text-sm text-amber-400 hover:text-amber-300 hover:underline ${hasDisplayName ? '' : 'font-mono'}`}
                            title={user.user_id}
                          >
                            {userLabel}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCopyId(user.user_id, 'user')}
                            className="group flex w-fit items-center gap-1 font-mono text-xs text-gray-500 hover:text-gray-300"
                            title={user.user_id}
                          >
                            {shortUserId(user.user_id)}
                            {copiedId === user.user_id ? (
                              <Check className="w-3 h-3 text-green-400" />
                            ) : (
                              <Copy className="w-3 h-3 opacity-0 transition-opacity group-hover:opacity-100" />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge className={`text-xs ${
                          user.tutorial_completed
                            ? 'bg-green-900/50 text-green-400 border border-green-700'
                            : 'bg-gray-800 text-gray-400 border border-gray-600'
                        }`}>
                          {user.tutorial_completed ? 'completed' : 'incomplete'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Badge className={`text-xs ${
                          user.case_generation_unlocked
                            ? 'bg-green-900/50 text-green-400 border border-green-700'
                            : 'bg-red-900/50 text-red-400 border border-red-700'
                        }`}>
                          {user.case_generation_unlocked ? 'unlocked' : 'locked'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        {user.admin_override && (
                          <Badge className="text-xs bg-amber-900/50 text-amber-400 border border-amber-700">
                            <Shield className="w-3 h-3 mr-1" />
                            admin
                          </Badge>
                        )}
                      </td>
                      <td className="p-3">
                        {user.unlimited_cases ? (
                          <Badge className="text-xs bg-purple-900/50 text-purple-400 border border-purple-700">
                            <Infinity className="w-3 h-3 mr-1" />
                            Unlimited
                          </Badge>
                        ) : (
                          <span className="text-sm text-gray-300">{user.daily_case_limit ?? 2}</span>
                        )}
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-gray-300">{user.cases_today ?? 0}</span>
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-gray-300">{user.cases_generated ?? 0}</span>
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-gray-400">{formatRelativeTime(user.last_activity)}</span>
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-8 px-3 text-xs font-medium transition-colors ${
                              user.admin_override
                                ? 'text-gray-500 cursor-not-allowed'
                                : user.case_generation_unlocked
                                  ? 'text-green-400 hover:text-red-400 hover:bg-red-900/30'
                                  : 'text-red-400 hover:text-green-400 hover:bg-green-900/30'
                            }`}
                            onClick={() => handleToggleTutorial(user, user.case_generation_unlocked, user.admin_override)}
                            disabled={isToggling || user.admin_override}
                            title={user.admin_override ? 'Admin users always have access' : user.case_generation_unlocked ? 'Lock case generation' : 'Unlock case generation'}
                          >
                            {isToggling ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : user.case_generation_unlocked ? (
                              <>
                                <Unlock className="w-4 h-4 mr-1" />
                                Unlocked
                              </>
                            ) : (
                              <>
                                <Lock className="w-4 h-4 mr-1" />
                                Locked
                              </>
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Admin dashboard table view
  const renderAdminDashboard = () => {
    const stats = getStats();
    const adminTabs = [
      { id: 'overview', label: 'Overview', icon: Activity },
      { id: 'cases', label: 'Cases', icon: Database },
      { id: 'sessions', label: 'Sessions', icon: List },
      { id: 'review', label: 'Review', icon: Shield },
      { id: 'failures', label: 'Failures', icon: FileWarning },
      { id: 'images', label: 'Images', icon: ImageIcon },
      { id: 'logs', label: 'Logs', icon: AlertTriangle, count: logsTotal },
      { id: 'users', label: 'Users', icon: Users },
    ];

    return (
      <div className="space-y-4">
        {/* Admin Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-gray-700 pb-2">
          {adminTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setAdminTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-t transition-colors ${
                  adminTab === tab.id
                    ? 'bg-gray-800 text-white border-b-2 border-amber-500'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.count > 0 && (
                  <span className="bg-amber-500 text-black text-xs px-1.5 py-0.5 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {adminTab === 'overview' ? (
          <div className="space-y-6">
            <AdminOverviewCards
              loading={adminOverviewLoading}
              overview={adminOverview}
              onRefresh={() => {
                fetchAdminOverview();
                fetchAdminCases(adminCasesFilters);
              }}
              onSelectTab={setAdminTab}
            />
            <AdminCasesTable
              cases={adminCases}
              loading={adminCasesLoading}
              total={adminCasesTotal}
              filters={adminCasesFilters}
              onFiltersChange={setAdminCasesFilters}
              onRefresh={() => fetchAdminCases(adminCasesFilters)}
              onViewCaseInfo={handleGeneratedCaseInfo}
              onCopyId={handleCopyId}
              copiedId={copiedId}
              onLoadCase={handleLoadAdminCase}
              onDeleteCase={handleDeleteAdminCase}
              loadingSession={loadingSession}
              deletingSession={deletingSession}
            />
          </div>
        ) : adminTab === 'cases' ? (
          <AdminCasesTable
            cases={adminCases}
            loading={adminCasesLoading}
            total={adminCasesTotal}
            filters={adminCasesFilters}
            onFiltersChange={setAdminCasesFilters}
            onRefresh={() => fetchAdminCases(adminCasesFilters)}
            onViewCaseInfo={handleGeneratedCaseInfo}
            onCopyId={handleCopyId}
            copiedId={copiedId}
            onLoadCase={handleLoadAdminCase}
            onDeleteCase={handleDeleteAdminCase}
            loadingSession={loadingSession}
            deletingSession={deletingSession}
          />
        ) : adminTab === 'review' ? (
          <AdminReviewQueue
            cases={reviewCases}
            loading={reviewCasesLoading}
            total={reviewCasesTotal}
            filters={reviewFilters}
            onFiltersChange={setReviewFilters}
            onRefresh={() => fetchReviewCases(reviewFilters)}
            onViewCaseInfo={handleGeneratedCaseInfo}
            onCopyId={handleCopyId}
            copiedId={copiedId}
          />
        ) : adminTab === 'failures' ? (
          <AdminGenerationFailures
            failures={generationFailures}
            stats={generationFailureStats}
            total={generationFailuresTotal}
            loading={generationFailuresLoading}
            statsLoading={generationFailureStatsLoading}
            filters={generationFailureFilters}
            onFiltersChange={setGenerationFailureFilters}
            onRefresh={() => {
              fetchGenerationFailures(generationFailureFilters);
              fetchGenerationFailureStats();
            }}
            onViewCaseInfo={handleGeneratedCaseInfo}
            onCopyId={handleCopyId}
            copiedId={copiedId}
          />
        ) : adminTab === 'images' ? (
          <AdminImageAudit
            images={adminImages}
            total={adminImagesTotal}
            loading={adminImagesLoading}
            filters={adminImageFilters}
            onFiltersChange={setAdminImageFilters}
            onRefresh={() => fetchAdminImages(adminImageFilters)}
            onViewCaseInfo={handleGeneratedCaseInfo}
            onCopyId={handleCopyId}
            copiedId={copiedId}
          />
        ) : adminTab === 'users' ? renderUsersView() : adminTab === 'logs' ? renderLogsView() : adminTab === 'sessions' ? (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <div className="text-2xl font-bold text-white">{stats.total}</div>
            <div className="text-xs text-gray-400 uppercase tracking-wide">Total Cases</div>
          </div>
          <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-700/50">
            <div className="text-2xl font-bold text-blue-400">{stats.inProgress}</div>
            <div className="text-xs text-gray-400 uppercase tracking-wide">In Progress</div>
          </div>
          <div className="bg-green-900/30 rounded-lg p-4 border border-green-700/50">
            <div className="text-2xl font-bold text-green-400">{stats.solved}</div>
            <div className="text-xs text-gray-400 uppercase tracking-wide">Solved</div>
          </div>
          <div className="bg-red-900/30 rounded-lg p-4 border border-red-700/50">
            <div className="text-2xl font-bold text-red-400">{stats.failed}</div>
            <div className="text-xs text-gray-400 uppercase tracking-wide">Failed</div>
          </div>
          {stats.deleted > 0 && (
            <div
              className="bg-orange-900/30 rounded-lg p-4 border border-orange-700/50 cursor-pointer hover:bg-orange-900/40 transition-colors"
              onClick={() => setFilters(f => ({ ...f, deleted: 'deleted' }))}
              title="Click to view deleted cases"
            >
              <div className="text-2xl font-bold text-orange-400">{stats.deleted}</div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">User Deleted</div>
            </div>
          )}
          <div className="bg-purple-900/30 rounded-lg p-4 border border-purple-700/50">
            <div className="text-2xl font-bold text-purple-400">{stats.uniqueUsers}</div>
            <div className="text-xs text-gray-400 uppercase tracking-wide">Users</div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && renderFilters()}

        {/* View Toggle */}
        <div className="flex justify-between items-center">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded transition-colors ${
              showFilters ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasActiveFilters && (
              <span className="bg-amber-500 text-black text-xs px-1.5 py-0.5 rounded-full font-medium">
                {activeFilterCount}
              </span>
            )}
          </button>

          <div className="flex gap-2">
            <Button
              variant={viewMode === 'table' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('table')}
              className="gap-2"
            >
              <List className="w-4 h-4" />
              Table
            </Button>
            <Button
              variant={viewMode === 'cards' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('cards')}
              className="gap-2"
            >
              <LayoutGrid className="w-4 h-4" />
              Cards
            </Button>
          </div>
        </div>

        {filteredCases.length === 0 ? (
          <div className="text-center py-12 bg-gray-900/30 rounded-lg border border-gray-700">
            <p className="text-gray-400">No cases match the current filters.</p>
            <button
              onClick={clearFilters}
              className="mt-2 text-sm text-amber-400 hover:text-amber-300"
            >
              Clear filters
            </button>
          </div>
        ) : viewMode === 'cards' ? (
          // Card view for admin (with owner badges)
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {filteredCases.map((caseItem) => {
              const statusInfo = getStatusBadge(caseItem.status);
              const isSoftDeleted = caseItem.deleted_by_user;
              const isForeignFeatured = caseItem.source === 'featured' && caseItem.is_own_case === false;
              const ownerLabel = getUserDisplayLabel({
                id: caseItem.owner_id,
                displayName: caseItem.owner_display_name,
                isOwn: caseItem.is_own_case,
              });

              return (
                <Card
                  key={caseItem.session_id}
                  className={`bg-gray-900 border-gray-700 hover:border-gray-500 transition-colors ${isSoftDeleted ? 'opacity-60 border-red-900/50' : ''}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className={`text-xl ${isSoftDeleted ? 'text-gray-400 line-through' : 'text-white'}`}>
                          {caseItem.victim_name}
                        </CardTitle>
                        {isSoftDeleted && (
                          <Badge className="mt-1 bg-red-900/50 text-red-300 text-xs border border-red-700">
                            User Deleted
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={statusInfo.className}>
                          {statusInfo.label}
                        </Badge>
                        {caseItem.owner_id && (
                          <Badge
                            variant="outline"
                            className={`text-xs ${caseItem.is_own_case ? 'border-green-500 text-green-400' : 'border-purple-500 text-purple-400'}`}
                            title={caseItem.owner_id}
                          >
                            <User className="w-3 h-3 mr-1" />
                            {ownerLabel}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div className="flex gap-2 mb-3">
                      <Badge className={getThemeColors(caseItem.theme, caseItem.is_tutorial, caseItem.source)}>
                        {formatThemeName(caseItem.theme, caseItem.is_tutorial, caseItem.source)}
                      </Badge>
                      <Badge variant="outline" className="capitalize text-gray-300 border-gray-600">
                        {displayDifficultyOf(caseItem)}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Turn {caseItem.turn_count || 0}/{caseItem.max_turns || 30}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(caseItem.started_at)}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        className="flex-1 min-h-touch"
                        onClick={() => handleLoadCase(caseItem)}
                        disabled={isForeignFeatured || loadingSession === caseItem.session_id || deletingSession === caseItem.session_id}
                        title={isForeignFeatured ? 'Featured progress belongs to another player' : undefined}
                      >
                        {loadingSession === caseItem.session_id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : caseItem.status === 'in_progress' ? (
                          'Continue'
                        ) : (
                          'Review'
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="min-h-touch min-w-touch border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
                        onClick={() => handleDeleteCase(caseItem)}
                        disabled={loadingSession === caseItem.session_id || deletingSession === caseItem.session_id}
                      >
                        {deletingSession === caseItem.session_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          // Table view for admin
          <div className="bg-gray-900/50 rounded-lg border border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700 bg-gray-800/50">
                    <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Victim</th>
                    <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">IDs</th>
                    <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">User</th>
                    <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Theme</th>
                    <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Difficulty</th>
                    <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Progress</th>
                    <th className="text-left p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Started</th>
                    <th className="text-right p-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {filteredCases.map((caseItem) => {
                    const statusInfo = getStatusBadge(caseItem.status);
                    const isLoading = loadingSession === caseItem.session_id;
                    const isDeleting = deletingSession === caseItem.session_id;
                    const progressPercent = getProgressPercent(caseItem.turn_count, caseItem.max_turns);
                    const isSoftDeleted = caseItem.deleted_by_user;
                    const isForeignFeatured = caseItem.source === 'featured' && caseItem.is_own_case === false;
                    const ownerLabel = getUserDisplayLabel({
                      id: caseItem.owner_id,
                      displayName: caseItem.owner_display_name,
                      isOwn: caseItem.is_own_case,
                    });

                    return (
                      <tr
                        key={caseItem.session_id}
                        className={`hover:bg-gray-800/30 transition-colors ${isSoftDeleted ? 'opacity-60 bg-red-900/10' : ''}`}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${isSoftDeleted ? 'text-gray-400 line-through' : 'text-white'}`}>
                              {caseItem.victim_name}
                            </span>
                            {isSoftDeleted && (
                              <Badge className="bg-red-900/50 text-red-300 text-xs border border-red-700">
                                User Deleted
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col gap-1">
                            {/* Session ID - matches LLM logs */}
                            <button
                              onClick={() => handleCopyId(caseItem.session_id, 'session')}
                              className="flex items-center gap-1 text-xs font-mono text-gray-400 hover:text-white transition-colors group"
                              title={`Session: ${caseItem.session_id}`}
                            >
                              <span className="text-gray-500">S:</span>
                              <span>{caseItem.session_id?.slice(-8)}</span>
                              {copiedId === caseItem.session_id ? (
                                <Check className="w-3 h-3 text-green-400" />
                              ) : (
                                <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                              )}
                            </button>
                            {/* Case ID */}
                            <button
                              onClick={() => handleCopyId(caseItem.case_id, 'case')}
                              className="flex items-center gap-1 text-xs font-mono text-gray-500 hover:text-gray-300 transition-colors group"
                              title={`Case: ${caseItem.case_id}`}
                            >
                              <span className="text-gray-600">C:</span>
                              <span>{caseItem.case_id?.slice(-8)}</span>
                              {copiedId === caseItem.case_id ? (
                                <Check className="w-3 h-3 text-green-400" />
                              ) : (
                                <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                              )}
                            </button>
                          </div>
                        </td>
                        <td className="p-3">
                          {caseItem.owner_id ? (
                            <span
                              className={`text-sm ${caseItem.is_own_case ? 'text-green-400' : 'text-purple-400'}`}
                              title={caseItem.owner_id}
                            >
                              {ownerLabel}
                            </span>
                          ) : (
                            <span className="text-gray-500">—</span>
                          )}
                        </td>
                        <td className="p-3">
                          <Badge className={`${getThemeColors(caseItem.theme, caseItem.is_tutorial, caseItem.source)} text-xs`}>
                            {formatThemeName(caseItem.theme, caseItem.is_tutorial, caseItem.source)}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <span className="text-gray-300 capitalize text-sm">{displayDifficultyOf(caseItem)}</span>
                        </td>
                        <td className="p-3">
                          <Badge className={`${statusInfo.className} text-xs`}>
                            {statusInfo.label}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-gray-700 rounded-full h-1.5">
                              <div
                                className="bg-blue-500 h-1.5 rounded-full"
                                style={{ width: `${progressPercent}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-400">
                              {caseItem.turn_count || 0}/{caseItem.max_turns || 30}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-sm text-gray-400">{formatDateTime(caseItem.started_at)}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-gray-400 hover:text-white hover:bg-gray-700"
                              onClick={() => handleLoadCase(caseItem)}
                              disabled={isForeignFeatured || isLoading || isDeleting}
                              title={isForeignFeatured
                                ? 'Featured progress belongs to another player'
                                : caseItem.status === 'in_progress' ? 'Continue' : 'Review'}
                            >
                              {isLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : caseItem.status === 'in_progress' ? (
                                <Play className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </Button>
                            {caseItem.source !== 'featured' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-amber-500 hover:text-amber-400 hover:bg-amber-900/30"
                                onClick={() => handleViewCaseInfo(caseItem)}
                                disabled={isLoading || isDeleting}
                                title="Case Info"
                              >
                                <Info className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-red-500 hover:text-red-400 hover:bg-red-900/30"
                              onClick={() => handleDeleteCase(caseItem)}
                              disabled={isLoading || isDeleting}
                              title="Delete"
                            >
                              {isDeleting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
          </>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className="min-h-screen text-white p-4 md:p-6 lg:p-8 relative"
      style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      }}
    >
      {/* Subtle texture overlay */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `
            radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
            radial-gradient(circle at 75% 75%, rgba(255,255,255,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      <div className={`mx-auto relative z-10 ${isAdmin ? 'max-w-7xl' : 'max-w-6xl'}`}>
        {/* Header */}
        <div className="flex items-center justify-between gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="flex items-center gap-3 md:gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="min-h-touch text-gray-400 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Back</span>
            </Button>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-wide" style={{ fontFamily: 'Playfair Display, serif' }}>
              {isAdmin ? 'Admin Dashboard' : 'Case Archive'}
            </h1>
          </div>
          {isAdmin && (
            <Badge variant="outline" className="border-amber-500 text-amber-400">
              Admin
            </Badge>
          )}
        </div>

        {!isAdmin && cases.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No cases yet.</p>
            <Button
              className="mt-4 min-h-touch"
              onClick={() => navigate('/')}
            >
              Start Your First Case
            </Button>
          </div>
        ) : isAdmin ? (
          renderAdminDashboard()
        ) : (
          renderUserCardView()
        )}
      </div>

      {/* Case Info Modal (Admin Only) - Combined generation info + diagnostics */}
      <CaseInfoModal
        isOpen={showCaseInfo}
        onClose={handleCloseCaseInfo}
        caseId={selectedCaseId}
        sessionId={selectedSessionId}
        onError={handleCaseInfoError}
      />

      {/* User Detail Modal */}
      {showUserDetail && (
        <UserDetailModal
          open={showUserDetail}
          onClose={handleCloseUserDetail}
          user={selectedUser}
          onUserUpdated={handleUserUpdated}
          onViewCaseInfo={handleGeneratedCaseInfo}
          onError={handleCaseInfoError}
        />
      )}
    </div>
  );
};
